import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

const categoryNameMap: Record<string, string> = {
  'gaoxiao-shipin': '搞笑视频',
  'gaoxiao-duanzi': '搞笑段子',
  'biaoqingbao': '表情包',
  'shenpinglun': '神评论',
  'qipa-shijian': '奇葩事件',
  'shadiao-shunjian': '沙雕瞬间',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">分类不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name}排行榜 - 搞笑排行榜`,
    description: category.description,
    url: `https://gaoxiao-paihangbang.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://gaoxiao-paihangbang.com/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <div className="min-h-screen pt-16">
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

      <div className="bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
          <div className="flex items-center gap-4">
            <span className="w-14 h-14 flex items-center justify-center rounded-xl bg-white/20 text-white text-2xl">
              <i className={category.icon}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">
                {category.name}排行榜
              </h1>
              <p className="text-white/70 text-sm mt-1">{category.description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <span className="text-sm text-foreground-500 whitespace-nowrap">分类筛选：</span>
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                cat.slug === slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {categoryArticles.length > 0 ? (
          <div className="flex flex-col gap-5">
            {categoryArticles.map((article) => (
              <ArticleCard
                key={article.slug}
                slug={article.slug}
                title={article.title}
                category={article.category}
                categoryName={categoryNameMap[article.category] || article.category}
                coverImage={article.coverImage}
                excerpt={article.excerpt}
                publishDate={article.publishDate}
                viewCount={article.viewCount}
                commentCount={article.commentCount}
                tag={article.tag}
                rank={article.rank}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-inbox-line text-2xl"></i>
            </div>
            <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
          </div>
        )}
      </div>
    </div>
  );
}